



  import { Component, Prop, mixins } from 'nuxt-property-decorator'
  import { namespace } from 'vuex-class'
  import { Account } from '@one/types'
  import AddToCartMixin from '~/mixins/AddToCartMixin'
  import { priceFormatter } from '~/utils/filters'
  import CurrentProductMixin from '~/mixins/CurrentProduct'
  import Client = Account.App.Responses.ClientRestView
  import Segment = Account.App.SegmentRestView

  const cart = namespace('cart')
  const wishlist = namespace('wishlist')
  const account = namespace('account')
  const layout = namespace('layout')
  const categories = namespace('categories')
  const stocks = namespace('stocks')

  @Component
  export default class OneCmsDynamicComponent extends mixins(AddToCartMixin, CurrentProductMixin) {
    name: string = 'OneCmsDynamicComponent.vue'

    @account.State currentUser: any
    @account.State client!: Client
    @account.Getter tradeCredit: any
    @cart.Getter getCartTotalPrice: any
    @cart.Getter getCurrentCart: any
    @cart.Getter getCurrentCartId: any
    @cart.Getter getBasicCarts: any
    @cart.Getter promoPoints: any
    @wishlist.Getter getCurrentWishlist: any
    @wishlist.Getter getCurrentWishlistId: any
    @wishlist.Getter getBasicWishlists: any
    @wishlist.Getter getWishlistProduct: any
    @wishlist.Action addProductToWishlist: any
    @layout.Getter isGrossSelected: any
    @layout.Getter getCurrency: any
    @categories.Getter getCategory: any
    @categories.State categories: any
    @categories.State selectedCategoryId: any
    @stocks.Getter getDefaultWarehouseId: any
    @stocks.Getter getWarehouse: any

    @Prop({
      required: true,
      type: String,
    })
    readonly html!: string

    @Prop({
      required: false,
      type: Object,
    })
    readonly htmlBox!: any // backward compatibility

    addToWishlist(productId: string, amount: number, wishlistId?: string) {
      this.addProductToWishlist({
        wishlistId: wishlistId || this.getCurrentWishlistId,
        newProduct: {
          productId: productId,
          amountOfProducts: amount,
        },
      })
        .then(() => {
          const addedProduct = this.getWishlistProduct(this.getCurrentWishlistId)(productId)
          this.$gtm.addToWishlist(
            {
              item_id: addedProduct.id,
              item_name: addedProduct.name,
              item_list_name: this.getCurrentWishlist.name,
              item_brand: addedProduct.brand?.name || '',
              category: this.$routes.getCategoryPath(addedProduct.canonicalCategory) || '',
              price: addedProduct.prices?.unitNet || 0,
              discount: addedProduct.prices?.catalogPriceNet - addedProduct.prices?.unitNet || 0,
              quantity: amount || 0,
            },
            addedProduct.prices.unitNet * amount,
          )
          this.$notify({
            group: 'alert',
            type: 'success',
            text: this.$t('product_page.alert_added_to_wishlist') as string,
          })
        })
        .catch((err: any) => {
          this.$logger.warn(err)
          this.$notify({
            group: 'alert',
            type: 'error',
            text: this.$t('errors.add_to_wishlist_error') as string,
          })
        })
    }

    get totalCartPrice(): string {
      return this.getCartTotalPrice(this.getCurrentCartId, this.isGrossSelected)
    }

    get scopedCart() {
      return {
        currentCart: this.getCurrentCart,
        totalCartPrice: this.totalCartPrice,
        carts: this.getBasicCarts,
        addToCart: this.addToCart,
        promoPoints: this.promoPoints,
      }
    }

    get scopedWishlist() {
      return {
        currentWishlist: this.getCurrentWishlist,
        wishlists: this.getBasicWishlists,
        addToWishlist: this.addToWishlist,
      }
    }

    get scopedConfig(): any {
      return {
        isRegistrationEnabled: this.$tenantConfig.isRegistrationEnabled,
        isB2CRegistrationEnabled: this.$tenantConfig.isB2CRegistrationEnabled,
        isPurchaseEnabled: this.$tenantConfig.isPurchaseEnabled,
        accessMode: this.$tenantConfig.accessMode,
        isPlatformOpened: this.$tenantConfig.isPlatformOpened,
        isCatalogOpened: this.$tenantConfig.isCatalogOpened,
        isPlatformClosed: this.$tenantConfig.isPlatformClosed,
        isGrossSelected: this.isGrossSelected,
      }
    }

    get scopedUtils(): any {
      return {
        isPurchaseEnabledOrAuthenticated: this.$utils.isPurchaseEnabledOrAuthenticated,
        isAnonymousAndPurchaseDisabled: this.$utils.isAnonymousAndPurchaseDisabled,
        isB2BAndPlatformIsNotOpened: this.$utils.isB2BAndPlatformIsNotOpened,
        isB2B: this.$utils.isB2B,
        isIndexPage: this.$utils.isIndexPage,
        currency: this.getCurrency,
        routes: {
          getCategoryPath: this.$routes.getCategoryPath,
          getManufacturerSearchUrl: this.$routes.getManufacturerSearchUrl,
          getManufacturerSearchUrlByCharacteristics: this.$routes.getManufacturerSearchUrlByCharacteristics,
          getProductUrl: this.$routes.getProductUrl,
          search: this.$routes.search,
          isExternalUrl: this.$routes.isExternalUrl,
        },
        formatter: {
          priceFormatter,
        },
      }
    }

    get scopedCatalog() {
      return {
        getCategoryById: this.getCategory,
        categories: this.categories,
        selectedCategoryId: this.selectedCategoryId,
      }
    }

    get scopedAccount() {
      return {
        isAuthenticated: this.$auth.isAuthenticated,
        logout: () => {
          this.$auth.logout()
          this.$router.push({
            path: this.$tenantConfig.isCatalogOpened
              ? `${this.localePath('/')}`
              : `${this.localePath('login')}`,
          })
        },
        user: {
          email: this.currentUser?.email,
          name: this.currentUser?.name,
          phone: this.currentUser?.phone,
          hasSegment: (name: string) => {
            if (!this.$auth.isAuthenticated) {
              return false
            }
            if (!this.client?.segments) {
              return false
            }
            const { mainSegment, additionalSegments } = this.client.segments
            return (
              mainSegment?.name === name ||
              !!additionalSegments?.find((segment: Segment) => segment.name === name)
            )
          },
        },
        client: {
          name: this.client?.name,
          billingAddress: this.client?.billingAddress,
        },
        tradeCredit: this.tradeCredit,
        manager: this.client && this.client.accountManager,
      }
    }

    get scopedStocks() {
      return {
        currentWarehouse: {
          id: this.getWarehouse(this.getDefaultWarehouseId)?.id,
          name: this.getWarehouse(this.getDefaultWarehouseId)?.name,
        },
      }
    }

    get dynamicComponent() {
      const html = this.html || (this.htmlBox && this.htmlBox.content) || null
      if (html) {
        const parent = this
        if (this.$product) {
          // @ts-ignore
          this.$attrs.product = this.$product
        }
        return {
          name: this.$attrs.id ?? 'AnonymousDynamicCmsBlock',
          template: html,
          // eslint-disable-next-line no-new-func
          // created: (new Function('ctx', 'console.log(this)')).bind(this.mockedThis),
          data() {
            return {
              $data: {},
            }
          },
          computed: {
            $one() {
              return {
                utils: parent.scopedUtils,
                account: parent.scopedAccount,
                cart: parent.scopedCart,
                wishlist: parent.scopedWishlist,
                config: parent.scopedConfig,
                catalog: parent.scopedCatalog,
                stock: parent.scopedStocks,
              }
            },
          },
        }
      }
      return {
        template: '<span></span>',
      }
    }
  }
